import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, ShieldX, Loader2, AlertTriangle } from 'lucide-react';
import { API_URL } from '../utils/api';

/**
 * Support decide landing page — reached from Approve/Deny magic links
 * inside the notification email. The token is the auth; no login is
 * required. Server rotates the token after use so the link is one-shot.
 */
export default function SupportDecidePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const decision = params.get('decision');
  const [state, setState] = useState({ loading: true, result: null, error: null });

  useEffect(() => {
    if (!token || !decision) {
      setState({ loading: false, result: null, error: 'Missing token or decision' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/support-mode/decide-token/${encodeURIComponent(token)}?decision=${encodeURIComponent(decision)}`,
          { method: 'POST' },
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed');
        setState({ loading: false, result: data, error: null });
      } catch (e) {
        setState({ loading: false, result: null, error: e.message || 'Failed' });
      }
    })();
  }, [token, decision]);

  const approved = state.result?.status === 'granted';
  const denied = state.result?.status === 'denied';

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden" data-testid="support-decide-page">
        <div
          className={`px-6 py-6 text-white text-center ${
            approved ? 'bg-gradient-to-r from-emerald-600 to-teal-600' :
            denied ? 'bg-gradient-to-r from-slate-700 to-slate-900' :
            state.error ? 'bg-gradient-to-r from-red-600 to-rose-600' :
            'bg-gradient-to-r from-indigo-600 to-fuchsia-600'
          }`}
        >
          {state.loading ? (
            <Loader2 className="w-10 h-10 mx-auto animate-spin" />
          ) : approved ? (
            <ShieldCheck className="w-10 h-10 mx-auto" />
          ) : denied ? (
            <ShieldX className="w-10 h-10 mx-auto" />
          ) : (
            <AlertTriangle className="w-10 h-10 mx-auto" />
          )}
          <h1 className="mt-3 text-xl font-bold">
            {state.loading
              ? 'Processing your decision…'
              : approved
              ? 'Support access approved'
              : denied
              ? 'Support access denied'
              : 'Something went wrong'}
          </h1>
        </div>
        <div className="p-6 text-center text-sm text-slate-700 space-y-3">
          {state.loading && <p>Please wait…</p>}
          {approved && (
            <>
              <p>
                <span className="font-semibold">{state.result.admin_name}</span> has been granted
                access to <span className="font-semibold">{state.result.org_name}</span> for 4 hours.
              </p>
              <p className="text-xs text-slate-500">
                Every action they take is written to your audit log. You can revoke access at any time
                from the banner in your dashboard.
              </p>
            </>
          )}
          {denied && (
            <p>
              The request from <span className="font-semibold">{state.result.admin_name}</span> was
              denied. They can't enter your account.
            </p>
          )}
          {state.error && (
            <p className="text-red-600">
              {state.error}
              <br />
              <span className="text-xs text-slate-500">
                The link may have expired or been used already.
              </span>
            </p>
          )}
          <Link
            to="/employer"
            className="inline-block mt-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            data-testid="support-decide-back"
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
